// R115 A1 — the MTR view's data model: the GET /api/mtr/calendar overlay types
// and the PURE functions that turn it into calendar pieces (chips, row badges,
// stay decorations, stat values, rail items). No DOM, no fetch — index.ts
// registers these; ui.ts draws the inspector sections / popovers / forms.
import type { CalendarCtx, ChipSpec, BadgeSpec, StayDecoration, RailItem, Tone } from '../registry';
import type { Property, Stay } from '../types';

export const LAYER = 'mtr';
export const STAGES_FALLBACK = ['new', 'contacted', 'qualified', 'touring', 'applied', 'screening', 'lease_sent', 'signed', 'active', 'lost', 'ended'];
export const TERMINAL = ['lost', 'ended'];
export const SETTLED = ['signed', 'active'];
export const CHANNELS: Array<{ key: 'zillow' | 'furnished_finder'; label: string; short: string }> = [
  { key: 'zillow', label: 'Zillow', short: 'Zillow' },
  { key: 'furnished_finder', label: 'Furnished Finder', short: 'FF' },
];

export interface MtrChipTone { tone: 'ok' | 'warn' | 'bad' | 'muted'; label: string; title: string }   // R116: + warn (a check clearing)
export interface MtrPayment { trackable: boolean; current: boolean; owed_count: number; owed_amount: number; owed: string[]; next_due: string | null; on_time: number; late: number; paid_count: number; logged: number; chip: MtrChipTone; clearing_count?: number; clearing_amount?: number }
/** R116 #4: the earliest open tenant follow-up — a date window + tone only, never note text. */
export interface MtrFollowUp { date: string; from: string | null; period: string | null; tone: 'upcoming' | 'now' | 'overdue'; label: string }
export interface MtrWo { id: string; title: string; status: string; priority: string; date: string | null }
export interface MtrTenant {
  tenant_id: string; listing_id: string; listing_name: string | null; source: 'auto' | 'manual'; unsaved?: boolean; name: string;
  lease_start: string | null; lease_end: string | null; in_house: boolean; status: 'active' | 'past'; stay_id: string | null; tenancy_key: string | null;
  monthly_amount: number | null; due_day: number | null; payment: MtrPayment; open_wos: { count: number; hot: boolean; items: MtrWo[] }; lead_id: string | null;
  notes_count?: number; follow_up?: MtrFollowUp | null; follow_ups?: { open: number; due: number; overdue: number };   // R116 #4
}
export interface MtrRec { month: number; rate: number | null; source: 'manual' | 'airroi' | null; basis: string; placeholder?: boolean }
export interface MtrListedCh { url: string | null; verified: boolean; listed_rate: number | null; listed_rate_at: string | null; listed_rate_by: string | null; scraped_price: number | null; scraped_at: string | null }
export type MtrListed = Partial<Record<'zillow' | 'furnished_finder', MtrListedCh | null>>;
export interface MtrBar { start: string; end: string; open_ended: boolean; clipped_l: boolean; clipped_r: boolean }
export interface MtrVacancy {
  listing_id: string; name: string; mtr_mode: 'dedicated' | 'seasonal'; mtr_source: string | null; status: 'vacant_now' | 'occupied' | 'leased';
  vacant_from: string | null; vacant_until: string | null; days_until_vacant: number | null; next_booking_start: string | null; open_ended: boolean;
  usable_nights: number | null; by_tenant: boolean; try_mtr: boolean; why: string; note: string | null; bar: MtrBar | null;
  recommended: MtrRec; listed: MtrListed; leads_open: number;
}
export interface MtrLeadTour { id: string; at: string; duration_min: number; status: 'scheduled' | 'done' | 'no_show' | 'cancelled'; with: string | null; note: string | null; listing_id?: string | null }
export interface MtrLead {
  id: string; name: string; stage: string; property_interest: string | null; move_in: string | null; term_months: number | null; offer_price: number | null;
  stage_changed_at: string | null; created_at: string | null; updated_at: string | null; tours_count: number; next_tour: { id: string; at: string; with: string | null } | null; tours: MtrLeadTour[];
  reopen_to?: string | null;
}
export interface MtrTour { id: string; lead_id: string; lead_name: string; listing_id: string | null; at: string; date: string; status: MtrLeadTour['status']; with: string | null; note: string | null; duration_min: number }
export interface MtrSummary { mtr_vacant: number; try_mtr_windows: number; try_mtr_dedicated: number; rent_owed: number; owing_tenants: number; untracked: number; tours_week: number; leads_open: number; rent_clearing?: number; clearing_tenants?: number }
export interface MtrOverlay {
  as_of: string; from: string; to: string; now_local: string | null;
  homes: string[]; tenants: MtrTenant[]; vacancies: MtrVacancy[]; rates: Record<string, { recommended: MtrRec; listed: MtrListed }>;
  leads: MtrLead[]; tours: MtrTour[]; summary: MtrSummary; stages?: string[]; basis?: Record<string, string>; errors?: Record<string, string>;
}

type LayerCtx = Pick<CalendarCtx, 'layer'>;
const ovMemo = new WeakMap<object, MtrOverlay>();
/** The loaded overlay, or null (layer off / not loaded / failed / an older server). Normalised once per payload. */
export function ov(ctx: LayerCtx): MtrOverlay | null {
  const o = ctx.layer<MtrOverlay>(LAYER);
  if (!o || typeof o !== 'object' || !Array.isArray(o.homes)) return null;
  const hit = ovMemo.get(o);
  if (hit) return hit;
  if (!o.rates || typeof o.rates !== 'object') o.rates = {};
  const n: MtrOverlay = {
    ...o,
    tenants: Array.isArray(o.tenants) ? o.tenants : [], vacancies: Array.isArray(o.vacancies) ? o.vacancies : [],
    leads: Array.isArray(o.leads) ? o.leads : [], tours: Array.isArray(o.tours) ? o.tours : [], rates: o.rates,
    summary: o.summary || { mtr_vacant: 0, try_mtr_windows: 0, try_mtr_dedicated: 0, rent_owed: 0, owing_tenants: 0, untracked: 0, tours_week: 0, leads_open: 0 },
  };
  ovMemo.set(o, n);
  return n;
}

// ── formatting (wall times are NEVER Date.parse'd: they carry no zone) ──────
const MONTH3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const money = (n: number | null | undefined) => (n == null || !Number.isFinite(Number(n)) ? '—' : '$' + Math.round(Number(n)).toLocaleString('en-US'));
export const compactMoney = (n: number) => (n >= 10000 ? `${Math.round(n / 1000)}k` : n >= 1000 ? `${(Math.round(n / 100) / 10).toFixed(1).replace(/\.0$/, '')}k` : String(Math.round(n)));
export const stageLabel = (s: string | null | undefined) => { const t = String(s || '').replace(/_/g, ' '); return t ? t[0].toUpperCase() + t.slice(1) : '—'; };
export const firstName = (s: string) => (String(s || '').trim().split(/\s+/)[0] || String(s || ''));
export const monthName = (m: number) => MONTH3[((Number(m) || 1) - 1 + 12) % 12];
export function wall(at: string | null | undefined): { date: string; h: number; mi: number } | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(String(at || ''));
  return m ? { date: m[1], h: Number(m[2]), mi: Number(m[3]) } : null;
}
export function timeLabel(at: string | null | undefined): string {
  const w = wall(at);
  if (!w) return '';
  return `${((w.h + 11) % 12) + 1}:${String(w.mi).padStart(2, '0')} ${w.h < 12 ? 'AM' : 'PM'}`;
}
/** "2pm" / "2:30pm" — chip labels are tight. */
export function shortTime(at: string | null | undefined): string {
  const w = wall(at);
  if (!w) return '';
  return `${((w.h + 11) % 12) + 1}${w.mi ? ':' + String(w.mi).padStart(2, '0') : ''}${w.h < 12 ? 'am' : 'pm'}`;
}
export const TOUR_LABEL: Record<string, string> = { scheduled: 'scheduled', done: 'done', no_show: 'no-show', cancelled: 'cancelled' };

// ── tenants ↔ stays ──────────────────────────────────────────────────────
/** A tenant ↔ calendar stay: hostaway stay_id first, else listing + check-in = lease start (legacy / manual). */
export function tenantMatchesStay(t: MtrTenant, s: Pick<Stay, 'id' | 'listing_id' | 'check_in'>): boolean {
  if (!t || !s || t.listing_id !== String(s.listing_id)) return false;
  if (t.stay_id) return String(t.stay_id) === String(s.id) || (!!t.lease_start && t.lease_start === s.check_in);
  return !!t.lease_start && t.lease_start === s.check_in;
}
/** Active tenants of one stay — an auto tenant (from the booking) first, then manual duplicates. */
export function tenantsForStay(o: MtrOverlay, s: Stay): MtrTenant[] {
  if (!s || s.kind !== 'stay') return [];
  return o.tenants.filter(t => t.status === 'active' && tenantMatchesStay(t, s))
    .sort((a, b) => (a.source === b.source ? 0 : a.source === 'auto' ? -1 : 1));
}
export const isOpenLead = (l: MtrLead) => !!l && !TERMINAL.includes(l.stage) && l.stage !== 'active';
export const recOf = (o: MtrOverlay, id: string): MtrRec | null => (o.rates[id] && o.rates[id].recommended) || null;
export const listedOf = (o: MtrOverlay, id: string): MtrListed => (o.rates[id] && o.rates[id].listed) || {};
export function verdict(listed: number | null | undefined, rate: number | null | undefined): { pct: number; v: 'good' | 'close' | 'low' } | null {
  if (!(Number(listed) > 0) || !(Number(rate) > 0)) return null;
  const pct = Math.round(Number(listed) / Number(rate) * 1000) / 10;
  return { pct, v: pct >= 100 ? 'good' : pct >= 95 ? 'close' : 'low' };
}
export function recText(r: MtrRec | null): string {
  if (!r || r.rate == null) return 'no rate yet';
  return `${money(r.rate)}/mo (${r.source === 'manual' ? 'manual' : 'AirROI'} · ${monthName(r.month)})`;
}
function listedLine(l: MtrListed): string {
  const parts = CHANNELS.map(c => { const x = l[c.key]; return x && x.listed_rate ? `${c.short} ${money(x.listed_rate)}` : null; }).filter(Boolean);
  return parts.length ? `listed ${parts.join(' · ')}` : 'not listed yet';
}

// R116 #4: an open follow-up as plain words for a title ("follow-up: check Oct 15–17 · overdue") — never note text.
export function followUpTitle(t: MtrTenant): string {
  const f = t.follow_up;
  if (!f || !f.label) return '';
  return `follow-up: ${f.label}${f.tone === 'overdue' ? ' · overdue' : f.tone === 'now' ? ' · now' : ''}`;
}
/** R116 #4: the rent badge carries a due follow-up — its tone rises to amber (now) / red (overdue), a ⏰ glyph, and the
 *  check-by window in its title. No 4th badge, no note text (DECISIONS R116 ADDENDUM §4). */
export function rentBadge(t: MtrTenant): { glyph: string; tone: Tone; title: string } {
  const c = t.payment.chip;
  const f = t.follow_up;
  const due = !!f && (f.tone === 'now' || f.tone === 'overdue');
  const rank: Record<string, number> = { bad: 3, warn: 2, ok: 1, muted: 0 };
  const fTone = f && f.tone === 'overdue' ? 'bad' : 'warn';
  const tone = (due && rank[fTone] > (rank[c.tone] ?? 0) ? fTone : c.tone) as Tone;
  const ft = followUpTitle(t);
  return { glyph: due ? '⏰' : '', tone, title: `${c.title}${ft ? ` · ${ft}` : ''}` };
}

// ── chips ────────────────────────────────────────────────────────────────
type ChipCtx = Pick<CalendarCtx, 'layer' | 'from' | 'to' | 'asOf' | 'staysOf' | 'nightTaken' | 'fmt'>;
function tenantTitle(t: MtrTenant, ctx: ChipCtx): string {
  const lease = `${t.lease_start ? ctx.fmt.md(t.lease_start) : '?'} → ${t.lease_end ? ctx.fmt.md(t.lease_end) : 'open-ended'}`;
  const rent = t.monthly_amount != null ? `${money(t.monthly_amount)}/mo` : 'no rent set';
  const ft = followUpTitle(t);
  return `MTR tenant ${t.name} · lease ${lease} · ${rent} · ${t.payment.chip.label}${t.payment.next_due ? ` · next due ${ctx.fmt.md(t.payment.next_due)}` : ''}${t.open_wos.count ? ` · ${t.open_wos.count} open WO${t.open_wos.count === 1 ? '' : 's'}` : ''}${ft ? ` · ${ft}` : ''}`;
}
function vacChip(o: MtrOverlay, v: MtrVacancy, start: string, end: string, seg: number, ctx: ChipCtx): ChipSpec {
  const vacantNowDed = v.mtr_mode === 'dedicated' && v.status === 'vacant_now';
  const rec = recOf(o, v.listing_id);
  const label = seg > 0 ? 'Try MTR' : vacantNowDed ? 'VACANT · MTR-only' : `Try MTR · ${rec && rec.rate != null ? `${money(rec.rate)}/mo` : 'no rate yet'}`;
  return {
    key: `mtr:vac:${v.listing_id}:${start}`, entity: { kind: 'mtr_vacancy', id: v.listing_id, listing_id: v.listing_id, date: start },
    listing_id: v.listing_id, start, end, tone: vacantNowDed ? 'bad' : 'accent', glyph: '🏠', label, dashed: true,
    title: `${v.name} — ${v.why}. Recommended ${recText(rec)}; ${listedLine(listedOf(o, v.listing_id))}.${v.note ? ` ${v.note}.` : ''} Tap for rates, + Lead, tours.`,
  };
}
export function chipsFor(p: Property, ctx: ChipCtx): ChipSpec[] {
  const o = ov(ctx);
  if (!o) return [];
  const id = String(p.listing_id);
  const out: ChipSpec[] = [];
  const toX = ctx.fmt.addDays(ctx.to, 1);
  const stays = ctx.staysOf(id);
  // 1. tenants with no stay bar of their own (direct leases; a booking outside the base payload)
  for (const t of o.tenants) {
    if (t.listing_id !== id || t.status !== 'active' || !t.lease_start) continue;
    if (stays.some(s => s.kind === 'stay' && tenantMatchesStay(t, s))) continue;
    const s = t.lease_start > ctx.from ? t.lease_start : ctx.from;
    const e = t.lease_end ? (t.lease_end < toX ? t.lease_end : toX) : toX;
    if (!(s < e)) continue;
    out.push({
      key: `mtr:ten:${t.tenant_id}`, entity: { kind: 'tenant', id: t.tenant_id, listing_id: id }, listing_id: id, start: s, end: e,
      tone: rentBadge(t).tone === 'bad' ? 'bad' : rentBadge(t).tone === 'warn' ? 'warn' : 'accent', glyph: '🔑', label: `${t.name} · ${t.payment.chip.label}${rentBadge(t).glyph ? ' ⏰' : ''}`,
      title: tenantTitle(t, ctx), badge: t.open_wos.count ? `🔧${t.open_wos.count}` : undefined, ring: t.open_wos.hot || undefined, dashed: !t.lease_end || undefined,
    });
  }
  // 2. try-MTR windows, split around nights the grid shows as taken (owner stays count as vacant on the radar)
  const v = o.vacancies.find(x => x.listing_id === id);
  if (v && v.try_mtr && v.bar) {
    let run: string | null = null, seg = 0, guard = 0;
    for (let d = v.bar.start; d < v.bar.end && guard < 400; d = ctx.fmt.addDays(d, 1), guard++) {
      const taken = ctx.nightTaken(id, d);
      if (!taken && run == null) run = d;
      if (taken && run != null) { out.push(vacChip(o, v, run, d, seg++, ctx)); run = null; }
    }
    if (run != null) out.push(vacChip(o, v, run, v.bar.end, seg++, ctx));
  }
  // 3. open leads moving in inside the window (the rest are in the rail)
  for (const l of o.leads) {
    if (!isOpenLead(l) || l.property_interest !== id || !l.move_in || l.move_in < ctx.from || l.move_in > ctx.to) continue;
    out.push({
      key: `mtr:lead:${l.id}`, entity: { kind: 'lead', id: l.id, listing_id: id, date: l.move_in }, listing_id: id, start: l.move_in,
      tone: SETTLED.includes(l.stage) ? 'ok' : 'info', glyph: '👤', label: `${firstName(l.name)} · ${stageLabel(l.stage)}`,
      title: `MTR lead ${l.name} · ${stageLabel(l.stage)} · move-in ${ctx.fmt.md(l.move_in)}${l.term_months ? ` · ${l.term_months} mo` : ''}${l.offer_price ? ` · offer ${money(l.offer_price)}/mo` : ''}`,
    });
  }
  // 4. tours on their day
  for (const t of o.tours) {
    if (t.listing_id !== id) continue;
    const off = t.status === 'no_show' || t.status === 'cancelled';
    out.push({
      key: `mtr:tour:${t.id}`, entity: { kind: 'tour', id: t.id, listing_id: id, date: t.date }, listing_id: id, start: t.date,
      tone: t.status === 'done' ? 'ok' : off ? 'muted' : 'info', glyph: '🗓', label: `${shortTime(t.at)} · ${firstName(t.lead_name)}`,
      title: `Tour ${ctx.fmt.wmd(t.date)} ${timeLabel(t.at)} · ${t.lead_name}${t.with ? ` · with ${t.with}` : ''} · ${TOUR_LABEL[t.status] || t.status}`,
      strike: off || undefined, movable: t.status === 'scheduled' && t.date >= ctx.asOf,
    });
  }
  return out;
}

// ── row badges + stay decoration ─────────────────────────────────────────
export function badgesFor(p: Property, ctx: LayerCtx): BadgeSpec[] {
  const o = ov(ctx);
  if (!o) return [];
  const id = String(p.listing_id);
  const out: BadgeSpec[] = [];
  const rec = recOf(o, id);
  const listed = listedOf(o, id);
  const anyListed = CHANNELS.some(c => listed[c.key] && listed[c.key]!.listed_rate);
  if (o.rates[id] && (rec && rec.rate != null || anyListed)) {
    out.push({ key: 'mtr_rate', glyph: '$', text: rec && rec.rate != null ? compactMoney(rec.rate) : '—', tone: 'accent', entity: { kind: 'listing', id, listing_id: id },
      title: `Recommended MTR rate ${recText(rec)} · ${listedLine(listed)} — tap to update listed rates` });
  }
  const v = o.vacancies.find(x => x.listing_id === id);
  if (v && v.leads_open) out.push({ key: 'mtr_leads', glyph: '👤', text: String(v.leads_open), tone: 'info', title: `${v.leads_open} open MTR lead${v.leads_open === 1 ? '' : 's'} for this home` });
  return out;
}
export function decorateStay(s: Stay, ctx: Pick<CalendarCtx, 'layer' | 'fmt'>): StayDecoration | null {
  const o = ov(ctx);
  if (!o) return null;
  const ts = tenantsForStay(o, s);
  if (!ts.length) return null;
  const t = ts[0];
  const badges: BadgeSpec[] = [
    { key: 'mtr_tenant', glyph: '🔑', text: 'Tenant', tone: 'accent', title: 'MTR tenant — tap for rent + work orders', entity: { kind: 'tenant', id: t.tenant_id, listing_id: t.listing_id } },
    { key: 'mtr_pay', text: t.payment.chip.label, ...rentBadge(t) },   // R116: carries a due follow-up (tone / ⏰ / title)
  ];
  if (t.open_wos.count) badges.push({ key: 'mtr_wo', glyph: '🔧', text: String(t.open_wos.count), tone: t.open_wos.hot ? 'bad' : 'warn', title: `${t.open_wos.count} open work order${t.open_wos.count === 1 ? '' : 's'} during this lease${t.open_wos.hot ? ' (urgent)' : ''}`, entity: { kind: 'tenant', id: t.tenant_id, listing_id: t.listing_id } });
  return { cls: 'mtrc-tenant-stay', badges, titleSuffix: ` · MTR tenant${t.payment.next_due ? ` · rent due ${ctx.fmt.md(t.payment.next_due)}` : ''}` };
}

// ── stats (KPI cards that double as the one filter) ─────────────────────
export function dedicatedVacant(o: MtrOverlay): Set<string> {
  return new Set(o.vacancies.filter(v => v.mtr_mode === 'dedicated' && v.status === 'vacant_now' && v.try_mtr).map(v => v.listing_id));
}
export function tryHomes(o: MtrOverlay): Set<string> { return new Set(o.vacancies.filter(v => v.try_mtr).map(v => v.listing_id)); }
export function owingHomes(o: MtrOverlay): Set<string> { return new Set(o.tenants.filter(t => t.payment.owed_amount > 0).map(t => t.listing_id)); }
/** This week's scheduled tours (today…+6). R115 review: built from EVERY open lead's tours (all in the payload), not the
 *  window-limited o.tours — a calendar paged past this week used to read "Tours this week 0". */
export function weekTours(o: MtrOverlay, asOf: string, addDays: (d: string, n: number) => string): MtrTour[] {
  const end = addDays(asOf, 6);
  const out: MtrTour[] = [];
  for (const l of o.leads || []) {
    if (!l || TERMINAL.includes(l.stage)) continue;
    for (const t of l.tours || []) {
      const d = String(t && t.at || '').slice(0, 10);
      if (!t || t.status !== 'scheduled' || d < asOf || d > end) continue;
      out.push({ id: t.id, lead_id: l.id, lead_name: l.name, listing_id: t.listing_id || l.property_interest, at: t.at, date: d, status: t.status, with: t.with || null, note: t.note || null, duration_min: t.duration_min || 60 });
    }
  }
  return out.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
}

// ── rails ────────────────────────────────────────────────────────────────
export function railTryMtr(ctx: Pick<CalendarCtx, 'layer' | 'property' | 'fmt'>): RailItem[] {
  const o = ov(ctx);
  if (!o) return [];
  return o.vacancies.filter(v => v.try_mtr).sort((a, b) => {
    const da = a.mtr_mode === 'dedicated' && a.status === 'vacant_now' ? 0 : 1, db = b.mtr_mode === 'dedicated' && b.status === 'vacant_now' ? 0 : 1;
    return da - db || String(a.vacant_from).localeCompare(String(b.vacant_from));
  }).map(v => ({
    key: `mtr:try:${v.listing_id}`, entity: { kind: 'listing', id: v.listing_id, listing_id: v.listing_id }, listing_id: v.listing_id,
    title: (ctx.property(v.listing_id)?.name) || v.name, sub: `${v.why} · rec ${recText(recOf(o, v.listing_id))}`,
    tone: (v.mtr_mode === 'dedicated' && v.status === 'vacant_now' ? 'bad' : 'warn') as Tone, date: v.vacant_from, search: `${v.name} try mtr vacant`,
  }));
}
export function railLeads(ctx: Pick<CalendarCtx, 'layer' | 'property' | 'fmt'>): RailItem[] {
  const o = ov(ctx);
  if (!o) return [];
  return o.leads.filter(isOpenLead).sort((a, b) => String(a.move_in || '9999').localeCompare(String(b.move_in || '9999')) || a.name.localeCompare(b.name)).map(l => {
    const home = l.property_interest ? (ctx.property(l.property_interest)?.name || 'home ' + l.property_interest) : 'no home yet';
    return {
      key: `mtr:lead:${l.id}`, entity: { kind: 'lead', id: l.id, listing_id: l.property_interest || undefined }, listing_id: l.property_interest,
      title: l.name, sub: `${stageLabel(l.stage)} · ${home} · ${l.move_in ? `move-in ${ctx.fmt.md(l.move_in)}` : 'no move-in date'}${l.next_tour ? ` · tour ${shortTime(l.next_tour.at)} ${ctx.fmt.md(l.next_tour.at.slice(0, 10))}` : ''}`,
      tone: (SETTLED.includes(l.stage) ? 'ok' : 'info') as Tone, date: l.move_in, search: `${l.name} ${home} ${l.stage}`,
    };
  });
}
export function railTours(ctx: Pick<CalendarCtx, 'layer' | 'property' | 'fmt' | 'asOf'>): RailItem[] {
  const o = ov(ctx);
  if (!o) return [];
  return weekTours(o, ctx.asOf, ctx.fmt.addDays).map(t => {
    const home = t.listing_id ? (ctx.property(t.listing_id)?.name || 'home ' + t.listing_id) : 'no home';
    return {
      key: `mtr:tourw:${t.id}`, entity: { kind: 'lead', id: t.lead_id, listing_id: t.listing_id || undefined, date: t.date }, listing_id: t.listing_id,
      title: `Tour · ${t.lead_name}`, sub: `${ctx.fmt.wmd(t.date)} ${timeLabel(t.at)} · ${home}${t.with ? ` · with ${t.with}` : ''}`,
      tone: 'info' as Tone, date: t.date, search: `${t.lead_name} ${home} tour`,
    };
  });
}
